import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserRole = 'family' | 'caregiver' | 'admin';

export interface User {
  id: string;
  email: string;
  fullName: string | null;
  role: UserRole;
  avatarUrl: string | null;
  status: string;
}

export interface OtpState {
  email: string;
  expiresAt: string;
  purpose: 'login' | 'register';
  fullName?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  otpState: OtpState | null;
  tempToken: string | null;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  setOtpState: (otpState: OtpState) => void;
  setTempToken: (tempToken: string) => void;
  clearOtpState: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  initialize: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,
      otpState: null,
      tempToken: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        }),

      setTokens: (accessToken: string, _refreshToken?: string) =>
        set({
          accessToken,
        }),

      setOtpState: (otpState) =>
        set({
          otpState,
        }),

      setTempToken: (tempToken) =>
        set({
          tempToken,
        }),

      clearOtpState: () =>
        set({
          otpState: null,
          tempToken: null,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          otpState: null,
          tempToken: null,
        }),

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      initialize: () => {
        const state = get();
        if (state.accessToken && !state.user) {
          // Token exists but no user - need to fetch user
          set({ isLoading: true });
          // TODO: Fetch user from API
        } else {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'caretrust-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Restore isAuthenticated based on whether user and token exist
          if (state.user && state.accessToken) {
            state.isAuthenticated = true;
          }
          state.setLoading(false);
        }
      },
    }
  )
);
